import React from 'react';
import './VMBypass.css';

const VMBypass = () => {
  return (
    <div className="vm-bypass-page">
      <div className="container">
        {/* Header */}
        <header className="header">
          <h1>Module 2: VM-Based Bypass</h1>
          <p className="subtitle">Maximum Autonomy & Desktop Proctor Protection</p>
          <span className="module-badge">Virtual Machine Solution</span>
        </header>

        {/* User Workflow Section */}
        <section className="workflow-section">
          <h2 className="section-title">How Users Work with Module 2</h2>
          
          <div className="workflow-diagram">
            {/* Step 1: Launch VM */}
            <div className="workflow-step">
              <div className="step-icon">🖥️</div>
              <h3 className="step-title">Step 1: Launch VM</h3>
              <p className="step-desc">Start virtual machine → Proctor software runs inside VM environment</p>
            </div>

            <div className="arrow-connector">
              <span className="arrow-label">Isolates</span>
              <div className="arrow-line"></div>
            </div>

            {/* Step 2: Proctor sees only VM */}
            <div className="workflow-step">
              <div className="step-icon">✅</div>
              <h3 className="step-title">Step 2: Clean View</h3>
              <p className="step-desc">Proctor sees only VM - real hardware, clean system, camera/mic work normally</p>
            </div>

            <div className="arrow-connector">
              <span className="arrow-label">Minimize</span>
              <div className="arrow-line"></div>
            </div>

            {/* Step 3: Use host system freely */}
            <div className="workflow-step">
              <div className="step-icon">🎯</div>
              <h3 className="step-title">Step 3: Freedom</h3>
              <p className="step-desc">Minimize VM window → Use your real system freely - search, chat, get help</p>
            </div>
          </div>
        </section>

        {/* Split View Section */}
        <section className="split-view-section">
          <h2 className="section-title">What Proctor Sees vs Reality</h2>
          
          <div className="split-container">
            {/* Proctor View */}
            <div className="view-panel proctor-view">
              <div className="view-header">
                <span className="view-icon">👁️</span>
                <h3 className="view-title">Proctor's View (Inside VM)</h3>
                <span className="view-status status-deceived">Deceived</span>
              </div>
              <div className="view-content">
                <div className="screen-mockup">
                  <div className="vm-window">
                    <div className="vm-header">
                      <span className="vm-icon">💻</span>
                      <span className="vm-title">Windows 11 Virtual Machine</span>
                    </div>
                    <div className="vm-content">
                      <div className="exam-title">Mathematics Exam - Active Session</div>
                      <div className="exam-text">Student appears focused. System clean. No violations detected.</div>
                    </div>
                  </div>
                </div>

                <div className="monitoring-list">
                  <div className="monitor-item">
                    <span className="monitor-icon">🖥️</span>
                    <div className="monitor-text">
                      <div className="monitor-label">System Check</div>
                      <div className="monitor-status">Real hardware detected</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">📹</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Camera/Microphone</div>
                      <div className="monitor-status">Working normally</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">🔍</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Process Monitor</div>
                      <div className="monitor-status">Only exam software</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">🚫</span>
                    <div className="monitor-text">
                      <div className="monitor-label">VM Detection</div>
                      <div className="monitor-status">Not a virtual machine</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reality View */}
            <div className="view-panel reality-view">
              <div className="view-header">
                <span className="view-icon">✨</span>
                <h3 className="view-title">Actual Reality (Host System)</h3>
                <span className="view-status status-active">Full Access</span>
              </div>
              <div className="view-content">
                <div className="screen-mockup">
                  <div className="vm-window minimized">
                    <div className="vm-header">
                      <span className="vm-icon">📦</span>
                      <span className="vm-title">VM Minimized (Running in background)</span>
                    </div>
                    <div className="vm-content">
                      <div className="exam-text" style={{fontSize: '0.85rem'}}>Exam continues inside VM...</div>
                    </div>
                  </div>

                  <div className="host-system">
                    <span className="host-label">Your Real System - Full Freedom</span>
                    <div className="host-content">
                      <div className="host-app">
                        <span className="app-icon">🌐</span>
                        <span className="app-name">Chrome</span>
                      </div>
                      <div className="host-app">
                        <span className="app-icon">💬</span>
                        <span className="app-name">Telegram</span>
                      </div>
                      <div className="host-app">
                        <span className="app-icon">📝</span>
                        <span className="app-name">Notes</span>
                      </div>
                      <div className="host-app">
                        <span className="app-icon">🤖</span>
                        <span className="app-name">ChatGPT</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="monitoring-list">
                  <div className="monitor-item">
                    <span className="monitor-icon">🌐</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Browse Freely</div>
                      <div className="monitor-status">Completely undetected</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">💬</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Use Messengers</div>
                      <div className="monitor-status">Get help from friends</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">🎮</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Any Application</div>
                      <div className="monitor-status">Zero restrictions</div>
                    </div>
                  </div>
                  <div className="monitor-item">
                    <span className="monitor-icon">🔒</span>
                    <div className="monitor-text">
                      <div className="monitor-label">Privacy Status</div>
                      <div className="monitor-status">Fully protected</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2 className="section-title">What Module 2 Bypasses</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon-box">🖥️</div>
                <h3 className="feature-title">Desktop Proctors</h3>
              </div>
              <p className="feature-desc">Bypasses full desktop monitoring software like Respondus LockDown Browser, ProctorU Desktop, and similar solutions</p>
              <ul className="feature-list">
                <li>Complete system isolation</li>
                <li>Real hardware emulation</li>
                <li>No virtualization detection</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon-box">🔍</div>
                <h3 className="feature-title">System Monitoring</h3>
              </div>
              <p className="feature-desc">Defeats comprehensive Windows system monitoring that browser proctors cannot achieve</p>
              <ul className="feature-list">
                <li>Process list inspection blocked</li>
                <li>Task manager access safe</li>
                <li>System calls intercepted</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon-box">📹</div>
                <h3 className="feature-title">Camera & Microphone</h3>
              </div>
              <p className="feature-desc">Webcam and audio pass through VM naturally - proctor sees you taking exam normally</p>
              <ul className="feature-list">
                <li>Hardware passthrough</li>
                <li>No lag or quality loss</li>
                <li>Natural appearance</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-icon-box">🛡️</div>
                <h3 className="feature-title">VM Detection Bypass</h3>
              </div>
              <p className="feature-desc">Modified by reverse engineers to show real hardware - impossible to detect as virtual machine</p>
              <ul className="feature-list">
                <li>Real hardware signatures</li>
                <li>Custom VM modifications</li>
                <li>Professional engineering</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="specs-section">
          <h2 className="specs-title">Module 2 Specifications</h2>
          
          <div className="specs-grid">
            <div className="spec-item">
              <div className="spec-icon">💪</div>
              <div className="spec-label">System Requirements</div>
              <div className="spec-value">Powerful PC needed for VM</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">📦</div>
              <div className="spec-label">Installation Size</div>
              <div className="spec-value">~2GB+ (Full OS included)</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">⏱️</div>
              <div className="spec-label">Setup Time</div>
              <div className="spec-value">~1 hour (one-time setup)</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">🔒</div>
              <div className="spec-label">Autonomy</div>
              <div className="spec-value">100% local - no internet needed</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">🛡️</div>
              <div className="spec-label">Reliability</div>
              <div className="spec-value">Maximum - no server dependency</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">⚡</div>
              <div className="spec-label">Internet</div>
              <div className="spec-value">No fast connection required</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">🎯</div>
              <div className="spec-label">Coverage</div>
              <div className="spec-value">Bypasses ALL desktop proctors</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">🔧</div>
              <div className="spec-label">Control</div>
              <div className="spec-value">Full control - your VM, your rules</div>
            </div>

            <div className="spec-item">
              <div className="spec-icon">🏆</div>
              <div className="spec-label">Success Rate</div>
              <div className="spec-value">Undetectable by design</div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="footer-banner">
          <p className="footer-text">Professional VM Solution for Complete Desktop Proctor Bypass</p>
        </div>
      </div>
    </div>
  );
};

export default VMBypass;
